import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  IconInfoEmpty,
  ProgressIndicator,
  RadioGroup,
  Switch,
} from '@ftdr/blueprint-components-react';
import getBrandConstants, { Brand } from '@constants/brands';
import Tooltip from '@components/tooltip/Tooltip';
import {
  AC_COVERAGE,
  SELLERS_COVERAGE,
  WARRANTY_TERM_LENGTH,
} from '@constants/formField-constants';
import CardBox from '@components/card/CardBox';
import { ProductFilters } from '@apis/models';
import { SelectedProductFilters } from '@components/card/CardNewOrderPlansCoverage';
import { getBrand } from '@helpers/brand.utils';
import { classNames } from '@utils';
import REText from '@components/wrappedBDS/REText';
import TradeServiceFee from '@components/input/TradeServiceFee';
import { FEFeatures, useOptimizelyFE } from '@context/OptimizelyContext/fe-index';

export interface FormNewOrderPlanFilterProps {
  /** hides all filters (if any) and displays a loading spinner instead */
  isLoadingFilters: boolean;
  /** should prevent the user from toggling sellers selection. applicable for existing contracts that already have sellers coverage. Cannot remove */
  disableSellersSelection: boolean;
  /** indicates what filters should be displayed */
  enabledFilters: ProductFilters;
  /** indicates what the selections are like for the filters */
  selectedFilters: SelectedProductFilters;
  /** fired whenever the seller filter was interacted */
  onChangeSeller: (includeSellers: boolean) => void;
  /** fired whenever the terms filter was interacted */
  onChangeTerms: (termSelection: string) => void;
  /** fired whenever the AC filter was interacted */
  onChangeAC: (includeAC: boolean) => void;
  /** fired whenever the TSF was interacted */
  onChangeServiceFee: (serviceFee: number) => void;
  isPastClosingDate?: boolean;
  isLoadingPlansCoverages?: boolean;
  serviceFees?: string[];
}

/** Note: children will reside inside the last card box of the filter */
const FormNewOrderPlanFilter: React.FC<FormNewOrderPlanFilterProps> = ({
  isLoadingFilters,
  enabledFilters,
  selectedFilters,
  onChangeSeller,
  onChangeTerms,
  onChangeAC,
  onChangeServiceFee,
  isPastClosingDate,
  isLoadingPlansCoverages,
  serviceFees,
  ...props
}) => {
  const brand = getBrand();

  const feoptimizely = useOptimizelyFE();
  const tradeServiceFeeVariables = feoptimizely.getFeatureVariables(FEFeatures.TradeServiceFeeEnabled, {
    brand: brand,
  });

  const [isTermYearSelectionAllowed, setIsTermYearSelectionAllowed] = useState<boolean>(false);

  let isFirstLoad = true;


  useEffect(() => {
    if (isFirstLoad && selectedFilters?.termYear) {
      isFirstLoad = true;
      onChangeTerms(selectedFilters.termYear.toString());
    }
  }, []);

  useEffect(() => {
    setIsTermYearSelectionAllowed(isPastClosingDate);
  }, [isPastClosingDate]);

  const switchStatus = useMemo(() => {
    if (isPastClosingDate || isLoadingPlansCoverages) {
      return true;
    }
    return props.disableSellersSelection;
  }, [isPastClosingDate, isLoadingPlansCoverages, props.disableSellersSelection]);

  const getRadioStatus = () => {
    if (isTermYearSelectionAllowed) {
      return false;
    }
    return selectedFilters?.includeSellersCoverage;
  };

  return (
    <>
      {isLoadingFilters ? (
        <div className="mt-4 w-full flex flex-col items-center">
          <ProgressIndicator
            variant="circular"
            size="large"
            label="Loading Plan Options..."
            labelPlacement="bottom"
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col justify-between md:flex-row">
            <div>
              {enabledFilters?.sellersCoverage && (
                <div className="flex items-center mx-4">
                  <Switch
                    id="card-new-order-plans-coverage__sellers-coverage-options-desired-form"
                    name="sellersCoverage"
                    label="Include Seller Coverage Option"
                    className="bds-override bds-select--label-variant"
                    disabled={switchStatus}
                    labelPlacement="left"
                    value="yes"
                    on={getRadioStatus()}
                    onChange={(e) => onChangeSeller(e.target.checked)}
                  />
                  {brand === Brand.AHS && (
                    <Tooltip
                      placement="right"
                      content={<p className="text-left">{SELLERS_COVERAGE}</p>}
                    >
                      <span className="ml-2">
                        <IconInfoEmpty color="interactive" />
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}

              {enabledFilters && (
                <div className="pt-6">
                  <CardBox
                    heading="Select Home Buyer's Coverage"
                    variant="none"
                    mobileVariant="none"
                  >
                    {enabledFilters.terms.length > 0 && (
                      <div className="w-max pb-2">
                        <RadioGroup
                          formField={true}
                          formFieldMessageId="wb_error-card-new-order-plans-coverage__warranty-term-form"
                          id="card-new-order-plans-coverage__warranty-term-form"
                          name="warrantyTermLength"
                          label="Warranty Term Length"
                          orientation="horizontal"
                          required={true}
                          secondaryAction={
                            brand === Brand.AHS &&
                            enabledFilters.terms &&
                            enabledFilters.terms.length > 1 && (
                              <Tooltip
                                placement="right"
                                content={<p className="text-left">{WARRANTY_TERM_LENGTH}</p>}
                              >
                                <span className="ml-2">
                                  <IconInfoEmpty color="interactive" />
                                </span>
                              </Tooltip>
                            )
                          }
                          radios={enabledFilters.terms.map((term) => ({
                            id: `card-new-order-plans-coverage__warranty-term-form-${term}`,
                            label: `${term} Year`,
                            value: `${term}`,
                          }))}
                          value={selectedFilters?.termYear ? `${selectedFilters.termYear}` : ''}
                          onChange={(e) => {
                            setIsTermYearSelectionAllowed(false);
                            onChangeTerms(e);
                          }}
                          disabled={isLoadingPlansCoverages}
                        />
                      </div>
                    )}

                    {enabledFilters.acCoverage && (
                      <>
                        <div className="inline-flex items-center justify-center pr-2">
                          <REText>A/C included in all plans</REText>
                          {brand === Brand.AHS && (
                            <Tooltip
                              placement="right"
                              content={<p className="text-left">{AC_COVERAGE}</p>}
                            >
                              <span className="ml-2">
                                <IconInfoEmpty color="interactive" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        <Button
                          id="card-new-order-plans-coverage__remove-ac-link"
                          label={selectedFilters?.includeACCoverage ? 'Remove' : 'Add'}
                          variant="ghost"
                          size="small"
                          onClick={() => onChangeAC(!selectedFilters?.includeACCoverage)}
                        />
                      </>
                    )}
                  </CardBox>
                </div>
              )}
            </div>

            {tradeServiceFeeVariables.enabled && (
              <div className="md:pr-20">
                <TradeServiceFee
                  selectedFee={selectedFilters.selectedFee}
                  onChange={onChangeServiceFee}
                  serviceFees={serviceFees}
                ></TradeServiceFee>
              </div>
            )}
          </div>
          {props.children}
        </>
      )}
    </>
  );
};

export default FormNewOrderPlanFilter;
